import {
    Alert,
    AlertIcon,
    Divider,
    Flex,
    Heading,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useTheme,
    VStack,
} from '@chakra-ui/react';
import { isEmpty, isEqual, keys, pick, values } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { updateMappings } from '../../../canopy-ui-components/api/mappings';
import CanopyButton from '../../../canopy-ui-components/components/CanopyButton/CanopyButton';
import CanopyIconButton from '../../../canopy-ui-components/components/IconButton/IconButton';
import * as ga from '../../../canopy-ui-components/google-analytics';
import { useCurrentColors } from '../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobileOrTablet } from '../../../canopy-ui-components/hooks/UseResponsive/UseResponsive';
import UserDetails, {
    UserDetailsType,
} from './../CreateAnalyticsMappings/UserDetails';
import WorkbookDetails, {
    WorkbookDetailsType,
} from './../CreateAnalyticsMappings/WorkbookDetails';

export type AnalyticsMapping = {
    userDetails: UserDetailsType;
    workbookDetails: WorkbookDetailsType[];
};

enum State {
    info = 'info',
    warning = 'warning',
    success = 'success',
    error = 'error',
}

interface Status {
    state?: State;
    message: string | null;
}

interface Props {
    userId: number[];
    data: any[];
    showModal: boolean;
    setShowModal: (flag: boolean) => void;
    globalProps: GlobalProps;
    refreshData: () => void;
}
function EditMappingModal({
    userId,
    data,
    showModal,
    setShowModal,
    globalProps,
    refreshData,
    ...props
}: Props) {
    const currentColors = useCurrentColors();
    const { t } = globalProps;
    const [isLoading, setIsLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const rawInputData = useMemo(() => {
        const temp: any = data?.map((item) => ({
            userDetails: {
                userId: item?.userId,
                userRoleId: item?.userRoleId,
                adminId: item?.adminId,
                adminRoleId: item?.adminRoleId,
                userName: item?.username,
                paOrRmName: item?.adminUsername,
                deviceType: item?.device,
            },
            workbookDetails: item.submenuWorkbook.map((workbook: any) => ({
                subMenuKey: workbook.submenuKey,
                workbookPath: workbook.workbookViewPath,
                defaultView: workbook?.defaultView || '',
            })),
        }));
        return temp;
    }, [data]);
    const theme = useTheme();

    const [status, setStatus] = useState<Status[]>();
    const isMobileOrTablet = useCheckForMobileOrTablet();
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
    const [analyticsMappings, setAnalyticsMappings] = useState<
        AnalyticsMapping[]
    >([
        {
            userDetails: {
                userId: 0,
                userRoleId: 0,
                adminId: 0,
                adminRoleId: 0,
                userName: '',
                paOrRmName: '',
                deviceType: '',
            },
            workbookDetails: [
                {
                    subMenuKey: '',
                    workbookPath: '',
                    defaultView: '',
                },
            ],
        },
    ]);

    useEffect(() => {
        const updatedAnalyticsMapping = data?.map((item) => ({
            userDetails: {
                userId: item?.userId,
                userRoleId: item?.userRoleId,
                adminId: item?.adminId,
                adminRoleId: item?.adminRoleId,
                userName: item?.username,
                paOrRmName: item?.adminUsername,
                deviceType: item?.device,
            },
            workbookDetails: item.submenuWorkbook.map((workbook: any) => ({
                subMenuKey: workbook.submenuKey,
                workbookPath: workbook.workbookViewPath,
                defaultView: workbook?.defaultView || '',
            })),
        }));
        setAnalyticsMappings(updatedAnalyticsMapping);
    }, [data]);
    useEffect(() => {
        const newFlag = isEqual(rawInputData, analyticsMappings);
        const isWorkbookValueEmpty = analyticsMappings[0].workbookDetails.some(
            (workbook: any) => {
                // remove defaultView key from workbook object
                const result = pick(
                    workbook,
                    keys({ subMenuKey: null, workbookPath: null })
                );
                return values(result).some(isEmpty);
            }
        );
        setIsSubmitButtonDisabled(newFlag || isWorkbookValueEmpty);
    }, [analyticsMappings]);

    const handleUserDetailsUpdate = (
        userDetails: UserDetailsType,
        index: number
    ): void => {
        let analyticsMappingsArr = [...analyticsMappings];
        analyticsMappingsArr[index] = {
            ...analyticsMappingsArr[index],
            userDetails: { ...userDetails },
        };
        setAnalyticsMappings(analyticsMappingsArr);
    };
    const handleWorkbookDetailsUpdate = (
        workbookDetails: WorkbookDetailsType,
        mappingsIndex: number,
        workbookIndex: number
    ): void => {
        const analyticsMappingsArr = [...analyticsMappings];
        analyticsMappingsArr[mappingsIndex].workbookDetails[workbookIndex] = {
            ...workbookDetails,
        };
        setAnalyticsMappings(analyticsMappingsArr);
    };

    const handleAddWorkbook = (mappingsIndex: number): void => {
        const newAnalyticsMappings = analyticsMappings.map(
            (mapping, mIndex) => {
                if (mIndex === mappingsIndex) {
                    return {
                        ...mapping,
                        workbookDetails: [
                            ...mapping.workbookDetails,
                            {
                                subMenuKey: '',
                                workbookPath: '',
                                defaultView: '',
                            },
                        ],
                    };
                } else {
                    return mapping;
                }
            }
        );
        setAnalyticsMappings(newAnalyticsMappings);
    };
    const handleDeleteWorkbook = (
        mappingsIndex: number,
        workbookIndex: number
    ): void => {
        const newAnalyticsMappings = analyticsMappings.map(
            (mapping, mIndex) => {
                if (mIndex === mappingsIndex) {
                    return {
                        ...mapping,
                        workbookDetails: [
                            ...mapping.workbookDetails.slice(0, workbookIndex),
                            ...mapping.workbookDetails.slice(workbookIndex + 1),
                        ],
                    };
                } else {
                    return mapping;
                }
            }
        );
        setAnalyticsMappings(newAnalyticsMappings);
    };

    const handleOnClose = () => {
        ga.event({
            action: 'Portfolio > Documents > Close Edit Document',
        });
        setShowModal(false);
    };

    useEffect(() => {
        showModal ? onOpen() : onClose();
    }, [showModal]);

    const handleSaveMappings = async () => {
        setIsLoading(true);
        setStatus([]);
        try {
            const result = await Promise.all(
                analyticsMappings.map(async (mapping, index) => {
                    const payload: any = {
                        id: data[index].id,
                        user_id: mapping.userDetails.userId,
                        device: mapping.userDetails.deviceType,
                        submenu_workbook: mapping.workbookDetails.map(
                            (workbook: WorkbookDetailsType) => ({
                                submenu_key: workbook.subMenuKey,
                                workbook_view_path: workbook.workbookPath,
                            })
                        ),
                    };

                    if (mapping.userDetails.adminId !== 0) {
                        payload.admin_id = mapping.userDetails.adminId;
                    }

                    return await updateMappings(payload);
                })
            );
            let temp_status: Status[] = [];

            result.map((response, index) => {
                temp_status.push({
                    state:
                        response?.state == 'error'
                            ? State.error
                            : State.success,
                    message:
                        response?.state == 'error'
                            ? response?.message
                            : t('ui.analytics-mappings.update-success'),
                });
            });
            setStatus(temp_status);
            setIsLoading(false);
            if (temp_status.every((res: any) => res?.state == State.success)) {
                setTimeout(() => {
                    setShowModal(false);
                    refreshData();
                }, 2000);
            }
        } catch (err) {
            console.log('main error:', err);
        }
    };

    return (
        <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={isOpen}
            size="6xl"
            scrollBehavior={'inside'}
            onClose={handleOnClose}>
            <ModalOverlay
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            />
            <ModalContent>
                <ModalHeader
                    color={currentColors.brand['10']}
                    fontSize="20px"
                    fontWeight="bold"
                    padding="15px">
                    <Flex alignItems="center">
                        <Image
                            src={theme?.images?.headerLogo}
                            maxWidth="100%"
                            width="auto"
                            height={'35px'}
                            mr="17.5px"
                        />
                        <Text width="100%">{t('edit-mappings')}</Text>
                    </Flex>
                    <Divider
                        borderColor={currentColors.brand['700']}
                        width="100%"
                        mb="0px"
                    />
                </ModalHeader>
                {
                    <ModalCloseButton
                        as={() => (
                            <CanopyIconButton
                                background="transparent"
                                cursor="pointer"
                                border="none"
                                display="inline-flex"
                                onClick={handleOnClose}
                                color={currentColors.brand['500']}
                                width="24px"
                                height="24px"
                                aria-label="remove"
                                icon="ActClose"
                                variant="ghost"
                                position="absolute"
                                top="15px"
                                right="15px"
                                isDisabled={isLoading}
                            />
                        )}
                    />
                }
                <ModalBody
                    padding="0 15px"
                    maxHeight="60vh"
                    overflow="auto">
                    {!isMobileOrTablet && (
                        <Flex>
                            <Flex
                                width="35%"
                                height="100%">
                                <Heading
                                    as="h6"
                                    size="md"
                                    my="10px"
                                    mx="0">
                                    {t('user-details')}
                                </Heading>
                            </Flex>
                            <Flex
                                width="82%"
                                height="100%">
                                <Heading
                                    as="h6"
                                    size="md"
                                    my="10px"
                                    mx="0">
                                    {t('workbook-details')}
                                </Heading>
                            </Flex>
                        </Flex>
                    )}
                    <VStack
                        spacing="30px"
                        width="100%">
                        {analyticsMappings?.map(
                            (
                                { userDetails, workbookDetails },
                                mappingsIndex
                            ) => {
                                return (
                                    <Flex
                                        key={mappingsIndex}
                                        width="100%"
                                        border={`1px solid ${currentColors.componentBorder}`}>
                                        <Flex
                                            align="flex-start"
                                            width="100%"
                                            p="10px"
                                            direction={[
                                                'column',
                                                'column',
                                                'column',
                                                'column',
                                                'row',
                                            ]}>
                                            {isMobileOrTablet && (
                                                <Heading
                                                    as="h6"
                                                    size="md"
                                                    m="0"
                                                    mt="10px"
                                                    px="20px"
                                                    alignSelf="flex-start">
                                                    {t('user-details')}
                                                </Heading>
                                            )}
                                            <HStack
                                                width={[
                                                    '100%',
                                                    '100%',
                                                    '100%',
                                                    '100%',
                                                    '40%',
                                                ]}
                                                justifyContent="space-between">
                                                <UserDetails
                                                    data={userDetails}
                                                    initialData={userDetails}
                                                    mappingsIndex={
                                                        mappingsIndex
                                                    }
                                                    updateUserDetails={
                                                        handleUserDetailsUpdate
                                                    }
                                                    globalProps={globalProps}
                                                    {...props}></UserDetails>
                                            </HStack>
                                            {!isMobileOrTablet && (
                                                <Divider
                                                    borderColor={
                                                        currentColors.brand[
                                                            '700'
                                                        ]
                                                    }
                                                    height="calc(100% - 20px)"
                                                    mr="20px"
                                                    orientation="vertical"
                                                />
                                            )}
                                            <VStack
                                                spacing="15px"
                                                width="100%"
                                                justifyContent="flex-start">
                                                {isMobileOrTablet && (
                                                    <>
                                                        <Divider
                                                            borderColor={
                                                                currentColors
                                                                    .brand[
                                                                    '700'
                                                                ]
                                                            }
                                                            width="calc(100% - 40px)"
                                                            mx="20px"
                                                        />
                                                        <Heading
                                                            as="h6"
                                                            size="md"
                                                            m="0"
                                                            px="20px"
                                                            alignSelf="flex-start">
                                                            {t(
                                                                'workbook-details'
                                                            )}
                                                        </Heading>
                                                    </>
                                                )}
                                                <HStack
                                                    width="100%"
                                                    alignItems="flex-start"
                                                    justify="flex-end">
                                                    {status &&
                                                        status[mappingsIndex]
                                                            ?.message && (
                                                            <Alert
                                                                status={
                                                                    status[
                                                                        mappingsIndex
                                                                    ]?.state
                                                                }
                                                                height="100%">
                                                                <AlertIcon />
                                                                {
                                                                    status[
                                                                        mappingsIndex
                                                                    ]?.message
                                                                }
                                                            </Alert>
                                                        )}
                                                    <CanopyButton
                                                        onClick={() =>
                                                            handleAddWorkbook(
                                                                mappingsIndex
                                                            )
                                                        }
                                                        height="30px"
                                                        data-test="add-workbook"
                                                        variant="outline"
                                                        alignSelf="flex-end"
                                                        fontSize="sm">
                                                        {t(
                                                            'ui.mm.add-workbook'
                                                        )}
                                                    </CanopyButton>
                                                </HStack>
                                                {workbookDetails?.map(
                                                    (workbookDetail, index) => {
                                                        return (
                                                            <WorkbookDetails
                                                                key={`index${index}`}
                                                                total={
                                                                    workbookDetails.length
                                                                }
                                                                data={
                                                                    workbookDetail
                                                                }
                                                                mappingsIndex={
                                                                    mappingsIndex
                                                                }
                                                                globalProps={
                                                                    globalProps
                                                                }
                                                                workbookIndex={
                                                                    index
                                                                }
                                                                updateWorkbookDetails={
                                                                    handleWorkbookDetailsUpdate
                                                                }
                                                                handleDeleteWorkbook={
                                                                    handleDeleteWorkbook
                                                                }></WorkbookDetails>
                                                        );
                                                    }
                                                )}
                                            </VStack>
                                        </Flex>
                                    </Flex>
                                );
                            }
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <HStack
                        spacing="15px"
                        alignSelf="flex-end"
                        justify="flex-end">
                        <CanopyButton
                            onClick={handleSaveMappings}
                            data-test="uploadButton"
                            width="120px"
                            height="30px"
                            isLoading={isLoading}
                            isDisabled={isSubmitButtonDisabled}
                            fontSize="sm">
                            {t('ui.mm.update')}
                        </CanopyButton>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default EditMappingModal;
